import { useState } from 'react';
import PageHeader from '../components/PageHeader';
import Icon from '../components/Icon';
import { useAppState } from '../components/StateProvider';
import { backendConfigured } from '../lib/supabase';
import type { HouseholdMember, StoreType } from '../types';

function splitList(value: string) { return value.split(',').map((x) => x.trim()).filter(Boolean); }

export default function Household() {
  const { state, reset, addMember, updateMember, removeMember, setKidFriendlyMode, addStore } = useAppState();
  const [showAdd, setShowAdd] = useState(false);
  const [name, setName] = useState('');
  const [role, setRole] = useState<'adult'|'child'>('adult');
  const [editing, setEditing] = useState<HouseholdMember | null>(null);
  const [allergies, setAllergies] = useState('');
  const [dislikes, setDislikes] = useState('');
  const [rules, setRules] = useState('');
  const [showStoreAdd, setShowStoreAdd] = useState(false);
  const [storeName, setStoreName] = useState('');
  const [storeType, setStoreType] = useState<StoreType>('supermarket');
  const [storeAddress, setStoreAddress] = useState('');
  const childCount = state.members.filter((member) => member.role === 'child').length;
  const kidFriendly = Boolean(state.kidFriendlyMode);

  async function submit() {
    if (!name.trim()) return;
    await addMember(name.trim(), role);
    setName(''); setRole('adult'); setShowAdd(false);
  }

  function beginEdit(member: HouseholdMember) {
    setEditing({ ...member });
    setAllergies(member.allergies.join(', '));
    setDislikes(member.dislikes.join(', '));
    setRules(member.dietaryRules.join(', '));
  }

  async function saveEdit() {
    if (!editing || !editing.name.trim()) return;
    await updateMember(editing.id, { ...editing, name: editing.name.trim(), allergies: splitList(allergies), dislikes: splitList(dislikes), dietaryRules: splitList(rules) });
    setEditing(null);
  }

  async function confirmRemove() {
    if (!editing) return;
    const approved = window.confirm(`Remove ${editing.name} from this Weekstead household? Their personal food preferences will also be removed.`);
    if (!approved) return;
    const removed = await removeMember(editing.id, editing.name);
    if (removed) setEditing(null);
  }

  async function submitStore() {
    if (!storeName.trim()) return;
    await addStore({ name: storeName.trim(), type: storeType, address: storeAddress.trim() || undefined });
    setStoreName(''); setStoreAddress(''); setStoreType('supermarket'); setShowStoreAdd(false);
  }

  return <div className="page">
    <PageHeader eyebrow="Household model" title={state.householdName} copy="The system learns each person separately, then optimizes food, quantities and stores for the household as a whole." />

    <section className="card kid-mode-card">
      <div className="section-title">
        <div><span className="eyebrow">Planning mode</span><h2>Kid-Friendly Mode</h2></div>
        <button type="button" className={`mode-toggle ${kidFriendly ? 'on' : ''}`} aria-pressed={kidFriendly} onClick={() => void setKidFriendlyMode(!kidFriendly)}>
          <span className="toggle-track"><span className="toggle-knob" /></span><strong>{kidFriendly ? 'On' : 'Off'}</strong>
        </button>
      </div>
      <p>{kidFriendly
        ? `Family planning is on${childCount ? ` for a household with ${childCount} ${childCount === 1 ? 'child' : 'children'}` : ''}. Weekstead favors approachable meals, milder defaults and flexible serving while keeping dinner enjoyable for adults.`
        : `${childCount ? `${childCount} ${childCount === 1 ? 'child is' : 'children are'} still in the household, but ` : ''}Kid-Friendly Mode is off. Individual allergies, dietary rules and dislikes are still respected.`}</p>
    </section>

    <section className="card settings-card people-card">
      <div className="section-title">
        <div><span className="eyebrow">People</span><h2>{state.members.length} household members</h2></div>
        <button className="outline compact" onClick={() => setShowAdd(!showAdd)}><Icon name="plus" /> Add member</button>
      </div>
      {showAdd && <div className="inline-form member-add-form">
        <label>Name<input value={name} onChange={(e) => setName(e.target.value)} placeholder="Household member" /></label>
        <label>Type<select value={role} onChange={(e) => setRole(e.target.value as 'adult'|'child')}><option value="adult">Adult</option><option value="child">Child</option></select></label>
        <button className="primary" onClick={() => void submit()}>Add to household</button>
      </div>}
      <div className="member-grid">{state.members.map((m) => <article key={m.id}>
        <div className="avatar">{m.name.charAt(0)}</div>
        <div className="member-copy"><strong>{m.name}</strong><span>{m.role} · {Math.round(m.portionFactor * 100)}% portion</span><small>{m.allergies.length ? `Allergies: ${m.allergies.join(', ')}` : 'No allergies recorded'}</small><button className="mini-link" onClick={() => beginEdit(m)}>Edit member</button></div>
      </article>)}</div>
    </section>

    {editing && <section className="card member-editor">
      <div className="section-title"><div><span className="eyebrow">Edit member</span><h2>{editing.name}</h2></div><button className="ghost" onClick={() => setEditing(null)}>Close</button></div>
      <div className="onboarding-grid">
        <label>Name<input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></label>
        <label>Type<select value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value as 'adult'|'child' })}><option value="adult">Adult</option><option value="child">Child</option></select></label>
        <label>Portion factor<input type="number" min="0.2" max="3" step="0.1" value={editing.portionFactor} onChange={(e) => setEditing({ ...editing, portionFactor: Math.max(.2, Number(e.target.value) || 1) })} /></label>
        <label>Allergies<input value={allergies} onChange={(e) => setAllergies(e.target.value)} placeholder="peanuts, shellfish" /></label>
        <label>Dietary rules<input value={rules} onChange={(e) => setRules(e.target.value)} placeholder="vegetarian, halal" /></label>
        <label className="wide-field">Dislikes<input value={dislikes} onChange={(e) => setDislikes(e.target.value)} placeholder="mushrooms, olives" /></label>
      </div>
      <div className="editor-actions"><button className="primary" onClick={() => void saveEdit()}>Save member</button><button className="remove-member" onClick={() => void confirmRemove()}>Remove from household</button></div>
    </section>}

    <div className="two-col settings-cols">
      <section className="card store-settings-card">
        <div className="section-title"><div><span className="eyebrow">Shopping rules</span><h2>{state.shopDaysPerWeek} runs per week</h2></div><button className="outline compact" onClick={() => setShowStoreAdd(!showStoreAdd)}><Icon name="plus" /> Add store</button></div>
        {state.locationLabel && <p className="muted-copy location-summary">Home area: {state.locationLabel}{state.groceryRadiusKm ? ` · ~${state.groceryRadiusKm} km automatic search radius` : ''}</p>}
        <p className="muted-copy">Your automatic radius only finds convenient nearby options. Add any store you intentionally travel to — such as Costco — and Weekstead can route suitable items there too.</p>
        {showStoreAdd && <div className="manual-store-form">
          <label>Store name<input value={storeName} onChange={(e) => setStoreName(e.target.value)} placeholder="Costco Richmond" /></label>
          <label>Store type<select value={storeType} onChange={(e) => setStoreType(e.target.value as StoreType)}><option value="supermarket">Supermarket</option><option value="warehouse">Warehouse / Costco-style</option><option value="asian-market">Asian market</option><option value="specialty">Specialty store</option></select></label>
          <label className="wide-field">Address or area <span>(optional)</span><input value={storeAddress} onChange={(e) => setStoreAddress(e.target.value)} placeholder="9151 Bridgeport Rd, Richmond" /></label>
          <button className="primary" onClick={() => void submitStore()}>Add this store</button>
        </div>}
        <div className="settings-list">{state.stores.map((s) => <div key={s.id}><Icon name="store" /><span><strong>{s.name}</strong><small>{s.distanceKm != null ? `${s.distanceKm.toFixed(1)} km · ` : ''}{s.formattedAddress ? `${s.formattedAddress} · ` : ''}{s.discovered ? 'Nearby discovery' : 'Household-selected store'} · priority {s.priority}{s.priceProvider ? ' · PriceCatcher+ ready' : ''}</small></span></div>)}</div>
      </section>
      <section className="card"><span className="eyebrow">Budget & price intelligence</span><h2>${state.weeklyBudget}/week</h2><div className="settings-list">
        <div><Icon name="budget" /><span><strong>Flexible grocery budget</strong><small>Optimizer targets household value, waste and trip efficiency — not simply the lowest sticker price.</small></span></div>
        <div><Icon name="store" /><span><strong>PriceCatcher+ active</strong><small>Live public pricing is enabled first for Real Canadian Superstore, No Frills and Loblaws. Receipt-learned prices remain a fallback, and future retailer feeds plug into the same comparison layer.</small></span></div>
        <div><span className={`status-dot ${backendConfigured ? '' : 'amber'}`} /><span><strong>{backendConfigured ? 'Weekstead Production connected' : 'Demo persistence active'}</strong><small>{backendConfigured ? 'Household sync, RLS and planner endpoints are live.' : 'Production backend environment variables not provisioned.'}</small></span></div>
      </div>{!backendConfigured && <button className="danger-link" onClick={reset}>Reset demo household</button>}</section>
    </div>
  </div>;
}
